// ─── Enums ───────────────────────────────────────────────────────────────────

export enum InvoiceType {
  HAULIER = "HAULIER",
  WAREHOUSE = "WAREHOUSE",
  SUNDRY = "SUNDRY",
  HAULIER_BULK = "HAULIER_BULK",
  VAT_HAULAGE = "VAT_HAULAGE",
  VAT_STORAGE = "VAT_STORAGE",
  HAULIER_COMPANION = "HAULIER_COMPANION",
}

export enum Haulier {
  PALLETWAYS = "PALLETWAYS",
  BOX_LOGISTICS = "BOX_LOGISTICS",
  TPN = "TPN",
}

export const HAULIER_LABELS: Record<Haulier, string> = {
  [Haulier.PALLETWAYS]: "Palletways",
  [Haulier.BOX_LOGISTICS]: "Box Logistics",
  [Haulier.TPN]: "TPN",
};

export enum InvoiceStatus {
  PROCESSING = "PROCESSING",
  VERIFIED = "VERIFIED",
  ISSUES_FOUND = "ISSUES_FOUND",
  ERROR = "ERROR",
}

export enum HaulierLineStatus {
  CORRECT = "CORRECT",
  OVERCHARGED = "OVERCHARGED",
  UNDERCHARGED = "UNDERCHARGED",
  RATE_NOT_FOUND = "RATE_NOT_FOUND",
  DUPLICATE = "DUPLICATE",
  UNKNOWN_SERVICE = "UNKNOWN_SERVICE",
}

export enum WarehouseLineStatus {
  CORRECT = "CORRECT",
  OVERCHARGED = "OVERCHARGED",
  UNDERCHARGED = "UNDERCHARGED",
  DUPLICATE = "DUPLICATE",
}

export enum ChargeType {
  PICK = "PICK",
  RECEIPT = "RECEIPT",
  RENTAL = "RENTAL",
}

export enum RateCardType {
  HAULIER = "HAULIER",
  WAREHOUSE = "WAREHOUSE",
}

export enum PalletType {
  FP = "FP",
  LP = "LP",
  HP = "HP",
  QP = "QP",
  MQP = "MQP",
  OSIZE = "OSIZE",
}

export type ServiceBand = "A" | "B";

export type FuelStatus = "NOT_APPLICABLE" | "UNCONFIRMED" | "CORRECT" | "OVERCHARGED" | "UNDERCHARGED";
export type CompanionStatus = "NOT_REQUIRED" | "AWAITING_PDF" | "AWAITING_CSV" | "ATTACHED";
export type SourceFormat = "PDF" | "CSV";

// ─── Entity Interfaces ──────────────────────────────────────────────────────

export interface User {
  id: string;
  email: string;
  display_name: string;
  created_at: string;
  last_login_at: string;
}

export interface InvoiceSummary {
  total_lines: number;
  correct_count: number;
  overcharged_count: number;
  undercharged_count: number;
  rate_not_found_count: number;
  duplicate_count: number;
  total_difference: number;
  total_overcharge: number;
  total_undercharge: number;
  [key: string]: unknown;
}

export interface WarehouseSummary {
  pick_count: number;
  pick_total: number;
  receipt_count: number;
  receipt_total: number;
  rental_count: number;
  rental_total: number;
}

export interface ReturnsSummary {
  count: number;
  total_cost: number;
}

export interface SundryLineItem {
  description: string;
  quantity: number;
  unit_price: number;
  net_amount: number;
}

/** The last diesel price entered on any invoice for a haulier, for pre-filling the fuel check field. */
export interface FuelLastUsed {
  diesel_pence: number;
  invoice_id: string;
  invoice_number: string;
  invoice_date: string;
  checked_by: string;
  checked_at: string;
  /** True when `invoice_date` falls in the same Monday-to-Sunday week as the invoice being viewed. */
  same_week: boolean;
}

export interface Invoice {
  id: string;
  uploaded_by: string;
  uploaded_at: string;
  invoice_type: InvoiceType;
  supplier: string;
  invoice_number: string;
  invoice_date: string;
  net_amount: number;
  vat_amount: number;
  gross_amount: number;
  file_key: string;
  file_hash: string;
  status: InvoiceStatus;
  processing_step: string;
  error_message?: string | null;
  processing_progress: number;
  total_lines: number;
  summary: InvoiceSummary | null;
  warehouse_summary?: WarehouseSummary;
  zone_summary?: ZoneSummary[];
  returns_summary?: ReturnsSummary;
  sundry_lines?: SundryLineItem[];
  duplicate_of: string | null;
  haulier?: Haulier;
  source_format?: SourceFormat;
  companion_status?: CompanionStatus;
  companion_file_key?: string | null;
  companion_note?: string | null;
  fuel_surcharge_pct?: number | null;
  fuel_surcharge_amount?: number | null;
  fuel_diesel_pence?: number | null;
  fuel_expected_pct?: number | null;
  fuel_expected_amount?: number | null;
  fuel_status?: FuelStatus;
  fuel_checked_by?: string | null;
  fuel_checked_at?: string | null;
  /** Last diesel price entered on any Box invoice — attached by the invoice-detail endpoint only. */
  fuel_last_used?: FuelLastUsed | null;
  pdf_net_amount?: number | null;
  pdf_vat_amount?: number | null;
  pdf_gross_amount?: number | null;
}

export interface ZonePalletBreakdown {
  pallet_type: string;
  count: number;
  total_invoiced: number;
  total_expected: number;
  difference: number;
  per_unit_difference: number;
}

export interface ZoneSummary {
  zone: number;
  lines: number;
  total_invoiced: number;
  total_expected: number;
  difference: number;
  pallet_breakdown: ZonePalletBreakdown[];
}

export interface HaulierLineItem {
  id: string;
  invoice_id: string;
  line_number: number;
  delivery_date: string;
  consignment_number: string;
  destination_name: string;
  destination_postcode: string;
  postcode_district: string;
  zone?: number;
  service_type: string;
  pallet_spec: string;
  pallet_type: PalletType;
  pallet_quantity: number;
  invoiced_amount: number;
  expected_amount: number;
  amount_difference: number;
  discount_amount: number;
  is_return?: boolean;
  status: HaulierLineStatus;
  notes: string;
}

export interface WarehouseLineItem {
  id: string;
  invoice_id: string;
  line_number: number;
  charge_id: string;
  created_at: string;
  charge_type: ChargeType;
  charge_code: string;
  receipt_reference: string;
  sales_order_reference: string;
  pick_list_reference: string;
  product_code: string;
  storage_unit_reference: string;
  invoiced_price: number;
  expected_price: number;
  price_difference: number;
  status: WarehouseLineStatus;
  notes: string;
}

export interface RateCard {
  id: string;
  effective_from: string;
  effective_to: string | null;
  uploaded_by: string;
  uploaded_at: string;
  card_type: RateCardType;
  file_key: string;
  haulier?: Haulier;
}

export interface HaulierRate {
  id: string;
  rate_card_id: string;
  postcode_district: string;
  zone: string;
  fp_prem_1: number | null;
  fp_prem_2: number | null;
  fp_prem_3: number | null;
  fp_prem_4: number | null;
  fp_econ_1: number | null;
  fp_econ_2: number | null;
  fp_econ_3: number | null;
  fp_econ_4: number | null;
  lp_prem_1: number | null;
  lp_prem_2: number | null;
  lp_prem_3: number | null;
  lp_prem_4: number | null;
  lp_econ_1: number | null;
  lp_econ_2: number | null;
  lp_econ_3: number | null;
  lp_econ_4: number | null;
  elp_prem: number | null;
  elp_econ: number | null;
  hp_prem: number | null;
  hp_econ: number | null;
  qp_prem: number | null;
  qp_econ: number | null;
  mqp_prem: number | null;
  mqp_econ: number | null;
}

export interface BoxServiceRates {
  qtr: number | null;
  half: number | null;
  full: number | null;
}

export interface BoxBandRate {
  id: string;
  rate_card_id: string;
  band: string;
  postcodes: string;
  services: Record<string, BoxServiceRates>;
}

export interface TpnRate {
  id: string;
  rate_card_id: string;
  postcode_district: string;
  zone: string;
  econ_qtr: number | null;
  econ_half: number | null;
  econ_light: number | null;
  econ_full: number | null;
  nd_qtr: number | null;
  nd_half: number | null;
  nd_light: number | null;
  nd_full: number | null;
}

export type AnyHaulierRate = HaulierRate | BoxBandRate | TpnRate;

export interface FuelEscalatorStep {
  pence: number;
  pct: number;
}

export interface HaulierConfig {
  haulier: Haulier;
  direct_rate_per_pallet: number | null;
  direct_postcode_areas: string[];
  fuel_surcharge_share: number | null;
  fuel_escalator: FuelEscalatorStep[];
  updated_by: string;
  updated_at: string;
}

export interface WarehouseRate {
  id: string;
  rate_card_id: string;
  charge_type: ChargeType;
  unit_price: number;
}

export interface ActivityLogEntry {
  id: string;
  date: string;
  timestamp: string;
  user_id: string;
  user_email: string;
  action_type: string;
  details: {
    invoice_id?: string;
    invoice_number?: string;
    rate_card_id?: string;
    description: string;
  };
}

export interface ServiceSurcharge {
  service_code: string;
  description: string;
  haulier: Haulier;
  base_band?: ServiceBand | null;
  surcharge_amount: number;
  per_pallet: boolean;
}

// ─── API Response Types ─────────────────────────────────────────────────────

export interface AuthResponse {
  token: string;
  user: User;
}

export interface PaginatedResponse<T> {
  items: T[];
  next_cursor?: string;
}

export interface AveragePalletCost {
  band: "A" | "B";
  pallet_type: string;
  average_cost: number;
  count: number;
}

export interface TypeStats {
  total_invoices: number;
  total_issues: number;
  total_value_verified: number;
  total_overcharge_value: number;
}

export interface DashboardStats {
  total_invoices: number;
  total_issues: number;
  total_value_verified: number;
  total_overcharge_value: number;
  processing_count: number;
  haulier: TypeStats;
  warehouse: TypeStats;
  average_pallet_costs: AveragePalletCost[];
}

export interface UploadUrlResponse {
  upload_url: string;
  file_key: string;
}

export interface ConsignmentInstance {
  invoice_id: string;
  invoice_number: string;
  invoice_date: string;
  amount: number;
  status: string;
}

export interface ConsignmentLookupResult {
  consignment_number: string;
  instances: ConsignmentInstance[];
}

export interface ActivityQueryResult {
  activities: ActivityLogEntry[];
  total_count: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

// ─── Haulier Report Types ────────────────────────────────────────────────────

export interface HaulierReportLine extends HaulierLineItem {
  invoice_number: string;
}

// ─── Report Types ────────────────────────────────────────────────────────────

export interface ReportInvoiceBreakdownItem {
  id: string;
  invoice_number: string;
  supplier: string;
  invoice_date: string;
  invoice_type: string;
  total_invoiced: number;
  total_expected: number;
  difference: number;
}

export interface ReportSummary {
  period: { from: string; to: string };
  invoice_count: number;
  total_lines: number;
  total_invoiced: number;
  total_expected: number;
  total_difference: number;
  zone_breakdown: ZoneSummary[];
  invoice_breakdown: ReportInvoiceBreakdownItem[];
}
